import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { memoCount, memoDeleteNote, memoNote, memoNotes, memoSaveNote, type MemoNoteRow } from '../api/memo'
import { toast } from './notify'

/**
 * Notele omului, in bara din dreapta - `MyMemoBoard` din `public/js/general.js`
 * + `#quick-sidebar-navigation` din `_layout/footer.php`, cu acelasi markup
 * (`memo-wrap`, `memo-list`, `memo-item`, `memo-title`, `memo-body` -
 * `public/css/memo_notes.css`).
 *
 * Doua ecrane in aceeasi bara: LISTA (cea mai recent atinsa prima, „New") si
 * NOTA (titlu, text, „Delete", inapoi la lista). Nota se scrie singura la
 * 800 ms dupa ultima tasta si la inchidere; o nota noua si goala nu se
 * scrie deloc. Cifra de pe insigna din antet (`#memo-badge-header`) e cea
 * din raspunsul serverului, nu numarata aici.
 *
 * Cand tabela `it_memo_notes` nu exista inca (`ready = false`), exista o
 * singura nota - cea veche din `it_memo` - iar „New" si „Delete" sunt stinse
 * si spun de ce, ca in portal.
 */
export const DONE_TYPING_MS = 800

function whenText(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso.replace(' ', 'T'))
  const diff = Math.round((Date.now() - then.getTime()) / 1000)
  if (Number.isNaN(diff)) return ''
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return then.toISOString().slice(0, 10)
}

export function MemoBadge({ count }: { count: number }) {
  return (
    <span id="memo-badge-header" className={`label bg-warning font-10${count > 0 ? '' : ' hidden'}`}>
      {count}
    </span>
  )
}

export function MemoBoard({ open, onClose, onCount }: { open: boolean; onClose: () => void; onCount: (n: number) => void }) {
  const [ready, setReady] = useState(true)
  const [rows, setRows] = useState<MemoNoteRow[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [note, setNote] = useState<{ id: number; title: string; body: string } | null>(null)
  const [state, setState] = useState('')
  const saved = useRef('')
  const timer = useRef<number | null>(null)
  const latest = useRef(note)
  latest.current = note

  const showList = useCallback(async () => {
    setNote(null)
    setRows(null)
    setFailed(false)
    try {
      const reply = await memoNotes()
      setReady(reply.ready)
      setRows(reply.notes)
    } catch {
      setFailed(true)
    }
  }, [])

  useEffect(() => {
    if (open) void showList()
  }, [open, showList])

  const save = useCallback(async (force = false): Promise<void> => {
    const current = latest.current
    if (current === null) return
    const stamp = `${current.title} ${current.body}`
    /* Nimic schimbat de la ultima scriere - nu se trimite nimic. */
    if (!force && stamp === saved.current) return
    /* O nota noua si complet goala nu se scrie deloc. */
    if (current.id < 1 && current.title.trim() === '' && current.body.trim() === '') return
    setState('Saving...')
    try {
      const reply = await memoSaveNote(current.id, current.title, current.body)
      saved.current = stamp
      if (latest.current !== null && latest.current.id < 1) {
        setNote({ ...latest.current, id: reply.id })
      }
      onCount(reply.notes)
      setState('Saved')
      window.setTimeout(() => setState(''), 1500)
    } catch (error) {
      setState('')
      toast(error instanceof ApiError ? error.message : 'Your note could not be saved.', 'Error', 'error')
    }
  }, [onCount])

  /* Scrierea singura, la 800 ms dupa ultima tasta. */
  const typed = (patch: Partial<{ title: string; body: string }>): void => {
    setNote((was) => (was === null ? was : { ...was, ...patch }))
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void save(), DONE_TYPING_MS)
  }

  const openNote = async (id: number): Promise<void> => {
    if (id > 0) {
      try {
        const loaded = await memoNote(id)
        saved.current = `${loaded.title} ${loaded.body}`
        setNote({ id: loaded.id, title: loaded.title, body: loaded.body })
      } catch {
        toast('That note could not be opened.', 'Error', 'error')
      }
    } else {
      saved.current = ' '
      setNote({ id: 0, title: '', body: '' })
    }
    setState('')
  }

  const backToList = async (): Promise<void> => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    await save()
    await showList()
    void memoCount().then((r) => onCount(r.notes)).catch(() => undefined)
  }

  const remove = async (): Promise<void> => {
    const current = latest.current
    if (current === null || current.id < 1) return
    if (!window.confirm('Delete this note?')) return
    if (timer.current !== null) window.clearTimeout(timer.current)
    try {
      const reply = await memoDeleteNote(current.id)
      onCount(reply.notes)
      saved.current = ''
      await showList()
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Your note could not be deleted.', 'Error', 'error')
    }
  }

  const close = (): void => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    void save()
    onClose()
  }

  useEffect(() => {
    if (!open) return
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <footer id="app-footer" className={open ? 'page-quick-sidebar-open' : ''}>
      <div className="page-quick-sidebar-wrapper quick-sidebar-navigation">
        <div className="page-quick-sidebar-actions">
          <span id="quick-sidebar-title" style={{ color: '#fff', padding: 13, fontSize: 16, fontWeight: 'bold', display: 'inline-block' }}>
            {note === null ? (
              <>
                <i className="flaticon-list-2" aria-hidden="true"></i> My notes
              </>
            ) : (
              <a href="#" className="memo-back color-white" onClick={(e) => { e.preventDefault(); void backToList() }}>
                <i className="fas fa-arrow-left" aria-hidden="true"></i> My notes
              </a>
            )}
          </span>
          <a className="close-sidebar" role="button" tabIndex={0} onClick={close} onKeyDown={(e) => e.key === 'Enter' && close()}>
            <i className="fas fa-indent"></i>
          </a>
        </div>
        <div className="page-quick-sidebar avatar-slide" style={{ height: '100%' }}>
          {open && note === null && (
            <div className="memo-wrap">
              <div className="memo-actions">
                <button
                  type="button"
                  className="memo-btn memo-new"
                  disabled={!ready}
                  title={ready ? 'New note' : 'Multiple notes are not available yet.'}
                  onClick={() => void openNote(0)}
                >
                  <i className="fas fa-plus" aria-hidden="true"></i> New
                </button>
              </div>
              {failed ? (
                <div className="memo-empty">Your notes could not be loaded.</div>
              ) : rows === null ? (
                <div className="memo-empty">Loading...</div>
              ) : rows.length === 0 ? (
                <div className="memo-empty">No notes yet. Write the first one.</div>
              ) : (
                <ul className="memo-list">
                  {rows.map((row) => (
                    <li className="memo-item" data-id={row.id} key={row.id} onClick={() => void openNote(row.id)}>
                      <span className="memo-item-title">{row.title}</span>
                      <span className="memo-item-snippet">{row.snippet}</span>
                      <span className="memo-item-when">{whenText(row.updated)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {open && note !== null && (
            <div className="memo-wrap">
              <div className="memo-actions">
                <span className="memo-state">{state}</span>
                <button
                  type="button"
                  className="memo-btn memo-delete"
                  disabled={!(note.id > 0 && ready)}
                  title={ready ? 'Delete this note' : 'Multiple notes are not available yet.'}
                  onClick={() => void remove()}
                >
                  <i className="fas fa-trash" aria-hidden="true"></i> Delete
                </button>
              </div>
              <input
                type="text"
                className="memo-title"
                maxLength={190}
                placeholder="Title"
                value={note.title}
                autoFocus={note.id < 1}
                onChange={(e) => typed({ title: e.target.value })}
              />
              <textarea
                className="memo-body"
                placeholder="write your notes here..."
                value={note.body}
                autoFocus={note.id > 0}
                onChange={(e) => typed({ body: e.target.value })}
              ></textarea>
            </div>
          )}
        </div>
      </div>
    </footer>
  )
}
